// src/pages/creditNote/CreditNoteListPage.jsx
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Receipt, Plus, Eye, Calendar, Clock, CheckCircle, XCircle, ShoppingBag, User, Phone } from 'lucide-react';
import { PageHeader, DataTable, FilterPanel } from '../../components/common';
import { getCreditNotes } from '../../services/api/offlineSales/creditNotes';
import { getCustomers } from '../../services/api/offlineSales/customers';
import { usePermissions } from '../../hooks/usePermissions';
import { useLocalization } from '../../contexts/LocalizationContext';
import { useAlert } from '../../contexts/AlertContext';
import { formatDate, formatTime } from '../../utils/dateUtils';
import { formatCurrency } from '../../utils/orderCalculations';
import DocNumberCell from '../procurement/components/DocNumberCell';
import tb from '../procurement/components/ProcurementTable.module.css';
import styles from './CreditNoteListPage.module.css';

const STATUS_ICONS = { issued: Clock, applied: CheckCircle, cancelled: XCircle };

// Return credit notes are auto-issued; manual ones are accented, mirroring DebitNoteListPage.
const sourceTypeStyle = { return: 'muted' };

// Only customerId is read server-side (rettzy-backend .../controllers/creditNote.controller.js)
// — no status/date-range/search query params exist yet, so only that one filter is wired here.
const statusConfig = {
  issued: { color: '#2563eb', bg: '#dbeafe' },
  applied: { color: '#10b981', bg: '#d1fae5' },
  cancelled: { color: '#ef4444', bg: '#fee2e2' },
};

const CreditNoteListPage = () => {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const { t } = useLocalization();
  const { error: showError } = useAlert();

  const [creditNotes, setCreditNotes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [customers, setCustomers] = useState([]);

  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 20,
    total: 0,
    pageSizeOptions: [20, 50, 100],
  });

  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({ customerId: '' });

  useEffect(() => {
    (async () => {
      try {
        const result = await getCustomers({ limit: 200 });
        setCustomers(result.customers || []);
      } catch (err) {
        console.error('Failed to load customers:', err);
      }
    })();
  }, []);

  useEffect(() => {
    fetchCreditNotes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.currentPage, pagination.pageSize, filters]);

  const fetchCreditNotes = async () => {
    try {
      setIsLoading(true);
      const params = {
        page: pagination.currentPage,
        limit: pagination.pageSize,
      };
      if (filters.customerId) params.customerId = filters.customerId;

      const response = await getCreditNotes(params);
      setCreditNotes(response.creditNotes || []);
      setPagination(prev => ({ ...prev, total: response.total || 0 }));
    } catch (err) {
      console.error('Failed to fetch credit notes:', err);
      if (err.response?.status === 403) {
        showError(t('errors.permissionDenied'));
      } else {
        showError(t('creditNote.failedToLoadCreditNotes'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  const handleClearFilters = () => {
    setFilters({ customerId: '' });
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  const handlePageSizeChange = (newPageSize) => {
    setPagination(prev => ({ ...prev, pageSize: newPageSize, currentPage: 1 }));
  };

  const hasActiveFilters = () => Object.values(filters).some(v => v !== '');

  const filterDefinitions = [
    {
      key: 'customerId',
      label: t('creditNote.customer'),
      type: 'select',
      placeholder: t('creditNote.allCustomers'),
      options: customers.map(c => ({ value: c.id.toString(), label: c.name })),
    },
  ];

  const columns = [
    {
      key: 'creditNoteNumber',
      header: t('creditNote.creditNoteNumber'),
      width: '170px',
      render: (row) => <DocNumberCell number={row.creditNoteNumber} to={`/offline-sales/credit-notes/${row.id}`} />,
    },
    {
      key: 'customer',
      header: t('creditNote.customer'),
      render: (row) => (
        <div className={tb.partyInfo}>
          <div className={tb.partyName}><User size={14} />{row.customer?.name || t('common.na')}</div>
          {row.customer?.phone && <div className={tb.partyMeta}><Phone size={12} />{row.customer.phone}</div>}
        </div>
      ),
    },
    {
      key: 'originalOrder',
      header: t('creditNote.originalOrder'),
      width: '160px',
      render: (row) => row.originalOrder?.orderNumber ? (
        <Link className={tb.docLink} to={`/offline-sales/orders/${row.originalOrder.id}`} onClick={(e) => e.stopPropagation()}>
          <ShoppingBag size={12} />{row.originalOrder.orderNumber}
        </Link>
      ) : <span className={tb.naText}>—</span>,
    },
    {
      key: 'sourceType',
      header: t('creditNote.sourceType'),
      width: '150px',
      render: (row) => (
        <div className={`${styles.sourceBadge} ${styles[`source_${sourceTypeStyle[row.sourceType] || 'accent'}`]}`}>
          {t(`creditNote.sourceType_${row.sourceType}`)}
        </div>
      ),
    },
    {
      key: 'amount',
      header: t('creditNote.amount'),
      width: '120px',
      align: 'right',
      render: (row) => <div className={tb.totalAmount}>{formatCurrency(row.amount)}</div>,
    },
    {
      key: 'appliedAmount',
      header: t('creditNote.appliedAmount'),
      width: '130px',
      align: 'right',
      render: (row) => <div className={tb.amountMuted}>{formatCurrency(row.appliedAmount)}</div>,
    },
    {
      key: 'status',
      header: t('common.status'),
      width: '130px',
      render: (row) => {
        const cfg = statusConfig[row.status] || statusConfig.issued;
        const StatusIcon = STATUS_ICONS[row.status] || Clock;
        return (
          <div className={tb.statusBadge} style={{ backgroundColor: cfg.bg, borderColor: cfg.color, color: cfg.color }}>
            <StatusIcon size={14} />
            <span>{t(`creditNote.status_${row.status}`)}</span>
          </div>
        );
      },
    },
    {
      key: 'createdAt',
      header: t('common.created'),
      width: '160px',
      render: (row) => (
        <div className={tb.dateCell}>
          <div className={tb.dateInfo}><Calendar size={12} />{formatDate(row.createdAt)}</div>
          <div className={tb.timeInfo}>{formatTime(row.createdAt)}</div>
        </div>
      ),
    },
  ];

  const tableActions = {
    render: (row) => (
      <div className={tb.actionsWrapper}>
        <Link
          to={`/offline-sales/credit-notes/${row.id}`}
          className={`${tb.actionButton} ${tb.viewButton}`}
          title={t('common.viewDetails')}
        >
          <Eye size={14} />
        </Link>
      </div>
    ),
    width: '90px',
    frozen: true,
  };

  return (
    <div className={styles.creditNoteListPage}>
      <PageHeader
        title={t('creditNote.allCreditNotes')}
        description={t('creditNote.viewAndManageAllCreditNotes')}
        icon={Receipt}
        actions={
          hasPermission('issue_credit_note') && (
            <Link to="/offline-sales/credit-notes/create" className={styles.primaryButton}>
              <Plus size={20} />
              {t('creditNote.issueCreditNote')}
            </Link>
          )
        }
      />

      <FilterPanel
        filters={filterDefinitions}
        values={filters}
        onChange={handleFilterChange}
        onClear={handleClearFilters}
        show={showFilters}
        onToggle={() => setShowFilters(!showFilters)}
        pageSizeConfig={{
          show: true,
          value: pagination.pageSize,
          onChange: handlePageSizeChange,
          options: pagination.pageSizeOptions,
        }}
      />

      <DataTable
        data={creditNotes}
        columns={columns}
        actions={tableActions}
        onRowClick={(row) => navigate(`/offline-sales/credit-notes/${row.id}`)}
        pagination={pagination}
        onPaginationChange={setPagination}
        loading={isLoading}
        emptyMessage={
          hasActiveFilters() ? t('creditNote.noCreditNotesFound') : t('creditNote.noCreditNotesYet')
        }
        emptyDescription={
          hasActiveFilters()
            ? t('creditNote.noCreditNotesFoundDescription')
            : t('creditNote.noCreditNotesYetDescription')
        }
        emptyIcon={<Receipt size={64} />}
        emptyAction={
          hasActiveFilters() ? (
            <button onClick={handleClearFilters} className={styles.primaryButton}>
              {t('common.clearFilters')}
            </button>
          ) : hasPermission('issue_credit_note') ? (
            <Link to="/offline-sales/credit-notes/create" className={styles.primaryButton}>
              <Plus size={18} />
              {t('creditNote.issueFirstCreditNote')}
            </Link>
          ) : null
        }
      />
    </div>
  );
};

export default CreditNoteListPage;
